import React, { useEffect, useState, useCallback } from "react";
import { GeoJsonLayer } from "@deck.gl/layers";
import "./Substations.css";

const DEFAULT_COLORS = {
  "0-50": [11, 12, 8, 255],
  "50-150": [46, 73, 43, 255],
  "150-400": [88, 150, 93, 255],
  "400+": [185, 240, 172, 255],
};

const COLOR_HEX = {
  "0-50": "#0b0c08",
  "50-150": "#2e492b",
  "150-400": "#58965d",
  "400+": "#b9f0ac",
};

const SELECTED_COLOR = [255, 255, 51, 255];

const RANGE_DEFINITIONS = [
  { label: "0-50", min: 0, max: 50, count: 0 },
  { label: "50-150", min: 50, max: 150, count: 0 },
  { label: "150-400", min: 150, max: 400, count: 0 },
  { label: "400+", min: 400, max: Infinity, count: 0 },
];

const Substations = ({
  wfsLayers = [],
  setWfsLayers,
  selectedRange,
  setSelectedRange,
}) => {
  const [ranges, setRanges] = useState(RANGE_DEFINITIONS);

  const getTrafoLayer = useCallback(() => {
    return wfsLayers.find((layer) => layer?.id?.includes("trafo_stanice"));
  }, [wfsLayers]);

  const getRangeLabel = (value) => {
    if (Number.isNaN(value)) return null;

    const foundRange = RANGE_DEFINITIONS.find(
      (range) => value >= range.min && value < range.max
    );

    return foundRange?.label || null;
  };

  const groupSubstationsByValue = useCallback(() => {
    const computedRanges = RANGE_DEFINITIONS.map((range) => ({ ...range }));
    const layer = getTrafoLayer();

    if (!Array.isArray(layer?.props?.data?.features)) {
      return computedRanges;
    }

    layer.props.data.features.forEach((feature) => {
      const value = Number(feature?.properties?.broj_mjernih_mjesta);
      const rangeLabel = getRangeLabel(value);

      const targetRange = computedRanges.find(
        (range) => range.label === rangeLabel
      );

      if (targetRange) {
        targetRange.count += 1;
      }
    });

    return computedRanges;
  }, [getTrafoLayer]);

  const updateColor = useCallback(
    (newSelectedRange) => {
      if (typeof setWfsLayers !== "function") return;

      setWfsLayers((prevLayers) =>
        prevLayers.map((layer) => {
          if (!layer?.id?.includes("trafo_stanice")) {
            return layer;
          }

          const originalData = layer?.props?.data;

          if (!Array.isArray(originalData?.features)) {
            return layer;
          }

          const dataWithRanges = {
            ...originalData,
            features: originalData.features.map((feature) => {
              const value = Number(feature?.properties?.broj_mjernih_mjesta);
              const rangeLabel = getRangeLabel(value);

              return {
                ...feature,
                properties: {
                  ...feature.properties,
                  range: rangeLabel,
                },
              };
            }),
          };

          return new GeoJsonLayer({
            ...layer.props,
            id: layer.id,
            data: dataWithRanges,
            getFillColor: (item) => {
              const featureRange = item?.properties?.range;

              if (newSelectedRange && featureRange === newSelectedRange) {
                return SELECTED_COLOR;
              }

              return DEFAULT_COLORS[featureRange] || [255, 255, 255, 255];
            },
            updateTriggers: {
              ...layer.props.updateTriggers,
              getFillColor: [newSelectedRange],
            },
          });
        })
      );
    },
    [setWfsLayers]
  );

  useEffect(() => {
    setRanges(groupSubstationsByValue());
  }, [groupSubstationsByValue]);

  useEffect(() => {
    updateColor(selectedRange);
  }, [selectedRange, updateColor]);

  const handleCheckboxChange = (e, range) => {
    setSelectedRange(e.target.checked ? range : null);
  };

  return (
    <div className="legend-container">
      <table className="legend-table">
        <thead>
          <tr>
            <th>Raspon</th>
            <th>Broj TS</th>
            <th>Selekcija</th>
          </tr>
        </thead>

        <tbody>
          {ranges.map((range) => (
            <tr key={range.label}>
              <td>
                <div className="legend-range-cell">
                  <span
                    className="legend-circle"
                    style={{ backgroundColor: COLOR_HEX[range.label] }}
                  />
                  <span>{range.label}</span>
                </div>
              </td>

              <td className="legend-count-cell">{range.count}</td>

              <td className="legend-checkbox-cell">
                <input
                  type="checkbox"
                  checked={selectedRange === range.label}
                  onChange={(e) => handleCheckboxChange(e, range.label)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Substations;